import React, { useState, useEffect, useMemo } from "react";
import UsersTable from "../UserTable/UserTable";
import UsersForm from "../UserForm/UserForm";
import ErrorDialog from "../../../ErrorDialog/ErrorDialog";
import Dialog from "../../../FormDialog/Dialog";
import { getHeaders } from "../../../../ApiHeaders/apiHeadersEntitlements";
import { getUserInfo } from "../../../../../services/authService-sighup";

import apiClientAdmin from "../../../../../services/apiClientAdmin";
import { getHeaders as getcompanyHeaders } from "../../../../ApiHeaders/apiHeaders";
import LoadingStyle from "../../../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../../../SplitPane/ResizableSplit";
import {
  EXTERNAL_TENANT_USERS_QUERY,
  isAmpEntitlementDomain,
  isExternalTenantUser,
} from "../../../constants/entitlementDomains";

interface Asset {
  asset_id: number;
  asset_name: string;
}

interface IdentityActivity {
  identity_id: string;
  identity_type: "user" | "service_principal";
  identity_status: "known" | "anonymous";
  last_active_utc: string | null;
  first_seen_utc: string | null;
  calls_last_365: number;
  enabled: string | null;
}

interface UserSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}

const UserSetup: React.FC<UserSetupProps> = ({
  registerTabSwitchGuard,
  canDelete = false
}) => {
  const [users, setUsers] = useState<any[]>([]);
  const [identityActivities, setIdentityActivities] = useState<IdentityActivity[]>([]);
  const apiUrl = process.env.REACT_APP_ENTITLEMENTS_API;

  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [currentUser, setCurrentUser] = useState<{ email: string; fullName: string } | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);

  const [entityType, setEntityType] = useState<"USER" | "SERVICE_PRINCIPAL">("USER");

  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);


  const [environment, setEnvironment] = useState<"DEV" | "STAGE" | "PROD">("DEV");

  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [initialRoles, setInitialRoles] = useState<number[]>([]);
  
  const [assetsForUser, setAssetsForUser] = useState<string[]>([]);
  const [foldersForUser, setFoldersForUser] = useState<string[]>([]);
  
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isAssetsLoading, setIsAssetsLoading] = useState(false);
  
  const fetchAssets = async () => {
    try {
      setIsAssetsLoading(true);
  
      const assetHeaders = await getcompanyHeaders();
  
      const response = await apiClientAdmin.get(
        `/assets/?page_size=500`,
        {
          headers: assetHeaders,
        }
      );
  
      const assetData: any[] = response.data?.response?.data ?? [];
  
      const mappedAssets: Asset[] = assetData.map((a: any) => ({
        asset_id: a.asset_id,
        asset_name: a.asset_name,
      }));
  
      setAssets(mappedAssets);
  
    } catch (err) {
      console.error("Failed to fetch assets:", err);
    } finally {
      setIsAssetsLoading(false);
    }
  };
  
  const fetchUserDetailedInfo = async (entraObjectId: string) => {
    try {
      const headers = await getHeaders();
  
      const response = await fetch(
        `${apiUrl}/user/entra/${entraObjectId}/detailed-info`,
        { headers }
      );
  
      const data: any = await response.json();
  
      const linkedRoles: { role_id: number }[] =
        data?.response?.data?.linked_roles ?? [];
  
      const linkedObjects: { object_id: number }[] =
        data?.response?.data?.linked_objects ?? [];
  
      const linkedFolders: { permission_object_name: string }[] =
        data?.response?.data?.linked_folders ?? [];
  
      // 🔹 1. Roles
      const roleIds: number[] = linkedRoles.map((r) => r.role_id);
      setSelectedRoles(roleIds);
      setInitialRoles(roleIds);
  
      // // 🔹 2. Distinct Object IDs
      // const objectIds: number[] = Array.from(
      //   new Set(linkedObjects.map((obj) => obj.object_id))
      // );
  
      // // 🔹 3. Map Object IDs → Asset Names
      // const assetNames: string[] = Array.from(
      //   new Set(
      //     assets
      //       .filter((asset) => objectIds.includes(asset.asset_id))
      //       .map((asset) => asset.asset_name)
      //   )
      // );
  
      // setAssetsForUser(assetNames);
      const objectIds: number[] = Array.from(
        new Set(linkedObjects.map((obj) => obj.object_id))
      );
      
      const assetNames: string[] = Array.from(
        new Set(
          objectIds
            .map((id) => assetMap.get(id))
            .filter((name): name is string => Boolean(name))
        )
      );
      
      setAssetsForUser(assetNames);
  
      // 🔹 4. Distinct Folder Names (permission_object_name)
      // 🔹 4. Distinct + Sorted Folder Names
      const folderNames: string[] = Array.from(
        new Set(
          linkedFolders.map(
            (f: { permission_object_name: string }) =>
              f.permission_object_name
          )
        )
      ).sort((a, b) => a.localeCompare(b));
  
      setFoldersForUser(folderNames);
  
    } catch (err) {
      handleApiError(err, "Failed to Fetch User Detailed Info");
    }
  };
  
  const fetchRoles = async () => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${apiUrl}/role`, { headers });
      const data = await response.json();

      if (data.response?.data) {
        setRoles(
          data.response.data.filter((r: any) => isAmpEntitlementDomain(r.domain))
        );
      }
    } catch (err) {
      handleApiError(err, "Failed to Fetch Roles");
    }
  };


  // useEffect(() => {
  //   const spUsers = users.filter((u) => u.is_service_principal === "Y");
  
  //   if (!spUsers.length) return;
  
  //   const envCount = { DEV: 0, STAGE: 0, PROD: 0 };
  
  //   spUsers.forEach((u) => {
  //     const name = u.login_name || "";
  //     if (name.endsWith("-DEV")) envCount.DEV++;
  //     else if (name.endsWith("-STAGE")) envCount.STAGE++;
  //     else if (name.endsWith("-PROD")) envCount.PROD++;
  //   });
  
  //   const detected = Object.entries(envCount).sort((a, b) => b[1] - a[1])[0][0];
  //   setEnvironment(detected as "DEV" | "STAGE" | "PROD");
  // }, [users]);
  useEffect(() => {
    const env = process.env.REACT_APP_ENV || "PROD";

    setEnvironment(env as "DEV" | "STAGE" | "PROD");
  }, []);
  



  const fetchCompanies = async () => {
    try {
      const headers = await getcompanyHeaders();
  
      const response = await apiClientAdmin.get("/company/", { headers });
  
      if (response.data?.response?.data) {
        setCompanies(response.data.response.data);
      } else {
        throw new Error("Invalid company response format");
      }
    } catch (err) {
      handleApiError(err, "Failed to Fetch Companies");
    }
  };
  
  

    useEffect(() => {
    const userInfo = getUserInfo();
    if (userInfo) {
        setCurrentUser({
        email: userInfo.email || "",
        fullName: userInfo.fullName || "Unknown User",
        });
    }
    }, []);


  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };

  //  Tab switch guard (same logic as UserGroup)
  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);

  //  API Error Handler (same style)
  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = "Invalid data submitted.";
        break;
      case 401:
        title = "Access Denied";
        details = "You're not authorized.";
        break;
      case 404:
        title = "Not Found";
        details = "Resource not found.";
        break;
      case 500:
        title = "Server Error";
        details = "Internal server error.";
        break;
      default:
        details = err.message || details;
        break;
    }

    setError({ title, details });
  };

  // Fetch Users
  const fetchUsers = async () => {
    setLoading(true);
    const headers = await getHeaders();
    try {
      const response = await fetch(
        `${apiUrl}/user/?${EXTERNAL_TENANT_USERS_QUERY}`,
        { headers }
      );
      const data = await response.json();
      if (data.response?.data) {
        setUsers(data.response.data.filter(isExternalTenantUser));
      }
    } catch (err) {
      handleApiError(err, "Failed to Fetch Users");
    } finally {
      setLoading(false);
    }
  };

  const fetchIdentityActivities = async () => {
    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${apiUrl}/entitlements/activity/identities?limit=500`,
        { headers }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch identity activity (${response.status})`);
      }

      const data = await response.json();
      const activities: IdentityActivity[] = data?.response?.data?.items ?? [];
      const activitiesByIdentity = new Map<string, IdentityActivity>();

      activities.forEach((activity) => {
        if (activity.identity_id) {
          activitiesByIdentity.set(activity.identity_id.toLowerCase(), activity);
        }
      });

      setIdentityActivities(Array.from(activitiesByIdentity.values()));
    } catch (err) {
      handleApiError(err, "Failed to Fetch Identity Activity");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchIdentityActivities();
    fetchCompanies();
    fetchRoles();
    fetchAssets();
  }, []);

  const assetMap = useMemo(() => {
    const map = new Map<number, string>();
    assets.forEach((a) => {
      map.set(a.asset_id, a.asset_name);
    });
    return map;
  }, [assets]);

  const fetchUserRoles = async (userId: number) => {
    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${apiUrl}/role-user-mapping/user/${userId}/roles`,
        { headers }
      );
  
      const data = await response.json();
  
      if (data.response?.data?.role_ids) {
        setSelectedRoles(data.response.data.role_ids);
        setInitialRoles(data.response.data.role_ids);
      } else {
        setSelectedRoles([]);
        setInitialRoles([]);
      }
    } catch (err) {
      handleApiError(err, "Failed to Fetch User Roles");
    }
  };
  
  const handleCreateUser = async (formData: any) => {
    try {
      const createdBy =
        currentUser?.email || currentUser?.fullName || "system";
  
      const baseHeaders = await getHeaders();
      const headers = { "Content-Type": "application/json", ...baseHeaders };
  
      //  SERVICE PRINCIPAL CREATION
      if (formData.type === "SERVICE_PRINCIPAL") {
        const payload = {
          ...formData.data,
          sys_created_by: createdBy,
        };
  
        const response = await fetch(
          `${apiUrl}/user/service_principal`,
          { method: "POST", headers, body: JSON.stringify(payload) }
        );
  
        const result = await response.json();
  
        if (!response.ok)
          throw new Error(result?.response?.message || "SP creation failed");
  
        const secret = result?.response?.data?.client_secret;
        const entraId = result?.response?.data?.entra_object_id;
        const userId = result?.response?.data?.user_id;
  
        const formattedName = `BETMExternal-API-${payload.client_name}-${environment}`;

        if (selectedRoles.length > 0 && userId) {
          await Promise.all(
            selectedRoles.map((roleId) =>
              fetch(`${apiUrl}/role-user-mapping`, {
                method: "POST",
                headers,
                body: JSON.stringify({
                  role_id: roleId,
                  user_id: userId,
                  enabled: "Y",
                  sys_created_by: createdBy,
                  sys_modified_by: createdBy,
                }),
              })
            )
          );
        }
  
        // FIRST set the full SP as selected (puts form into view mode)
        setSelectedUser({
          user_id: userId,
          login_name: formattedName,
          company_id: payload.company_id,
          enabled: payload.enabled,
          entra_object_id: entraId,
          is_service_principal: "Y",
        });
  
        //  THEN push secret (form now ready to show it)
        if (secret) {
          setGeneratedSecret(secret);
        }
  
        fetchUsers();
        handleClearSelection();
  
        setDialog({
          open: true,
          title: "Success",
          message: `Service Principal "${payload.client_name}" created.`,
          confirmButtonText: "Close",
          onConfirm: closeDialog,
        });
  
        return;
      }

      // EXISTING USER CREATION (DB ONLY)

      // if (formData.type === "EXISTING_USER") {

      //   const payload = {
      //     ...formData.data,
      //     sys_created_by: createdBy,
      //     sys_modified_by: createdBy,
      //   };

      //   const response = await fetch(`${apiUrl}/user/`, {
      //     method: "POST",
      //     headers,
      //     body: JSON.stringify(payload),
      //   });

      //   const result = await response.json();

      //   if (!response.ok) {
      //     throw new Error(result?.response?.message || "Failed to create user");
      //   }

      //   const newUserId = result?.response?.data?.user_id;

      //   if (selectedRoles.length > 0) {
      //     await Promise.all(
      //       selectedRoles.map((roleId) =>
      //         fetch(`${apiUrl}/role-user-mapping`, {
      //           method: "POST",
      //           headers,
      //           body: JSON.stringify({
      //             role_id: roleId,
      //             user_id: newUserId,
      //             enabled: "Y",
      //             sys_created_by: createdBy,
      //             sys_modified_by: createdBy,
      //           }),
      //         })
      //       )
      //     );
      //   }

      //   await fetchUsers();
      //   handleClearSelection();

      //   setDialog({
      //     open: true,
      //     title: "Success",
      //     message: `Existing user "${payload.login_name}" added successfully.`,
      //     confirmButtonText: "Close",
      //     onConfirm: closeDialog,
      //   });

      //   return;
      // }
  
      //  NORMAL USER CREATION
      const payload = {
        ...formData.data,
        sys_created_by: createdBy,
        sys_modified_by: createdBy,
      };
      
      const response = await fetch(`${apiUrl}/user/azure_db_user`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result?.response?.message || "Failed to create user");
      }
      
      // 🔹 1️⃣ Extract newly created user_id
      const newUserId = result?.response?.data?.user_id;
      
      if (!newUserId) {
        throw new Error("User created but user_id missing in response");
      }
      
      // 🔹 2️⃣ Create Role Mappings (if roles selected)
      if (selectedRoles.length > 0) {
        await Promise.all(
          selectedRoles.map((roleId) =>
            fetch(`${apiUrl}/role-user-mapping`, {
              method: "POST",
              headers,
              body: JSON.stringify({
                role_id: roleId,
                user_id: newUserId,
                enabled: "Y",
                sys_created_by: createdBy,
                sys_modified_by: createdBy,
              }),
            })
          )
        );
      }
      
      // 🔹 3️⃣ Refresh Users
      await fetchUsers();
      handleClearSelection();
      
      // 🔹 4️⃣ Reset Role State
      setSelectedRoles([]);
      setInitialRoles([]);
      
      // 🔹 5️⃣ Success Dialog
      setDialog({
        open: true,
        title: "Success",
        message: `User "${payload.invitedUserDisplayName}" successfully invited.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Create User");
    }
  };
  
  
  
  
  
  //  Update User
  const handleUpdateUser = async (userId: number, updatedUser: any) => {
    const baseHeaders = await getHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...baseHeaders,
    };
  
    try {
      // 🔹 1. Update User
      const response = await fetch(`${apiUrl}/user/${userId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(updatedUser),
      });
  
      if (!response.ok) {
        throw new Error("Failed to update user");
      }
  
      // 🔹 2. ROLE DIFF LOGIC
      const rolesToAdd = selectedRoles.filter(
        (roleId) => !initialRoles.includes(roleId)
      );
  
      const rolesToRemove = initialRoles.filter(
        (roleId) => !selectedRoles.includes(roleId)
      );
  
      // 🔹 3. Create New Role Mappings
      await Promise.all(
        rolesToAdd.map((roleId) =>
          fetch(`${apiUrl}/role-user-mapping`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              role_id: roleId,
              user_id: userId,
              enabled: "Y",
              sys_created_by: updatedUser.sys_modified_by,
              sys_modified_by: updatedUser.sys_modified_by,
            }),
          })
        )
      );
  
      // 🔹 4. Delete Removed Role Mappings
      await Promise.all(
        rolesToRemove.map((roleId) =>
          fetch(
            `${apiUrl}/role-user-mapping/role/${roleId}/user/${userId}`,
            {
              method: "DELETE",
              headers: baseHeaders,
            }
          )
        )
      );
  
      // 🔹 5. Reset Initial Roles Snapshot
      setInitialRoles(selectedRoles);
  
      // 🔹 6. Refresh Users List
      await fetchUsers();
      handleClearSelection();
  
      // 🔹 7. Success Dialog
      setDialog({
        open: true,
        title: "Success",
        message: `User "${updatedUser.login_name}" successfully updated.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Update User");
    }
  };

  //  Delete User
  // const handleDeleteUser = async (userId: number, loginName: string) => {
  //   const headers = await getHeaders();

  //   try {
  //     const response = await fetch(`${apiUrl}/user/${userId}`, {
  //       method: "DELETE",
  //       headers,
  //     });

  //     if (!response.ok) throw new Error("Delete failed");

  //     fetchUsers();
  //     setSelectedUser(null);
  //     setSelectedRow(null);

  //     setDialog({
  //       open: true,
  //       title: "Deleted",
  //       message: `User "${loginName}" deleted successfully.`,
  //       confirmButtonText: "Close",
  //       onConfirm: closeDialog,
  //     });
  //   } catch (err) {
  //     handleApiError(err, "Failed to Delete User");
  //   }
  // };
  const handleDeleteUser = async (userId: number, loginName: string) => {
    const headers = await getHeaders();
  
    try {
  
      // 1️⃣ Fetch roles linked to user
      const roleResponse = await fetch(
        `${apiUrl}/role-user-mapping/user/${userId}/roles`,
        { headers }
      );
  
      if (!roleResponse.ok) {
        throw new Error("Failed to fetch user roles");
      }
  
      const roleData = await roleResponse.json();
  
      const roleIds: number[] =
        roleData?.response?.data?.role_ids || [];
  
      // 2️⃣ Delete role-user mappings
      await Promise.all(
        roleIds.map((roleId) =>
          fetch(
            `${apiUrl}/role-user-mapping/role/${roleId}/user/${userId}`,
            {
              method: "DELETE",
              headers,
            }
          )
        )
      );
  
      // 3️⃣ Delete the user
      const deleteUserResponse = await fetch(
        `${apiUrl}/user/${userId}`,
        {
          method: "DELETE",
          headers,
        }
      );
  
      if (!deleteUserResponse.ok) {
        throw new Error("Failed to delete user");
      }
  
      // 4️⃣ Refresh UI
      await fetchUsers();
      handleClearSelection();
  
      setSelectedUser(null);
      setSelectedRow(null);
  
      setDialog({
        open: true,
        title: "Deleted",
        message: `User "${loginName}" deleted successfully.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Delete User");
    }
  };


  const handleRowSelect = (user: any) => {
    setGeneratedSecret(null);
  
    const type =
      user.is_service_principal === "Y" ? "SERVICE_PRINCIPAL" : "USER";
  
    setEntityType(type);
  
    const proceed = () => {
      setSelectedUser(user);
      setSelectedRow(user.user_id);

      if (user.entra_object_id) {
        fetchUserDetailedInfo(user.entra_object_id);
      } else {
        fetchUserRoles(user.user_id);
        setAssetsForUser([]);
        setFoldersForUser([]);
      }
    };
  
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message: "You have unsaved changes. Discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          closeDialog();
          proceed();
        },
        onCancel: closeDialog,
      });
    } else {
      proceed();
    }
  };
  

  const handleClearSelection = () => {
    setIsFormDirty(false);
  
    setSelectedUser(null);
    setSelectedRow(null);
  
    setSelectedRoles([]);
    setInitialRoles([]);
  
    setAssetsForUser([]);
    setFoldersForUser([]);
  
    setGeneratedSecret(null);
  
    setEntityType("USER"); // optional reset
  };

  const handleRevokeServicePrincipal = async (entraObjectId: string) => {
    try {
      const headers = await getHeaders();

      const response = await fetch(
        `${apiUrl}/user/service_principal/${entraObjectId}/revoke`,
        { method: "POST", headers }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.response?.message || "Revoke failed");
      }

      await fetchUsers();
      handleClearSelection();

      setDialog({
        open: true,
        title: "Access Revoked",
        message: `Service principal access has been revoked. The account is disabled and all client secrets have been removed.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Failed to Revoke Service Principal");
    }
  };

  const handleEntityToggle = () => {
    const nextType = entityType === "USER" ? "SERVICE_PRINCIPAL" : "USER";
  
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message: "You have unsaved changes. Switching will discard them. Continue?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setEntityType(nextType);
          setSelectedUser(null);
          setSelectedRow(null);
          setGeneratedSecret(null);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setEntityType(nextType);
      setSelectedUser(null);
      setSelectedRow(null);
      setGeneratedSecret(null);
    }
  };

  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <UsersTable
          users={users}
          identityActivities={identityActivities}
          companies={companies}
          selectedRow={selectedRow}
          onRowSelect={handleRowSelect}
          onClearSelection={handleClearSelection}
          // entityType={entityType}
          // onEntityTypeChange={handleEntityToggle}
        />
      </div>

      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        <UsersForm
          selectedUser={selectedUser}
          users={users}
          onCreateUser={handleCreateUser}
          onUpdateUser={handleUpdateUser}
          onDeleteUser={handleDeleteUser}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode: "create" | "edit" | "view" | null) =>
            setIsCreateMode(mode === "create")
          }
          onClearSelection={handleClearSelection}
          entityType={entityType}
          onEntityTypeChange={setEntityType}
          generatedSecret={generatedSecret}
          setGeneratedSecret={setGeneratedSecret}
          companies={companies}
          environment={environment}
          roles={roles}
          selectedRoles={selectedRoles}
          onRolesChange={setSelectedRoles}
          assetsForUser={assetsForUser}
          foldersForUser={foldersForUser}
          canDelete={canDelete}
          onRevokeServicePrincipal={handleRevokeServicePrincipal}
        />
      </div>

      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}

      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default UserSetup;
